import { useEffect, useState } from "react";
import { Plus, Home, Gift, Clock3 } from "lucide-react";
import { api, errMsg, fmtDate, fmtMoney, titleCase } from "@/lib/api";
import { PageHeader, StatCard, Stamp, EmptyState, Field } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export function SlotChip({ slot, time, className }) {
  if (!slot) return <span className={cn("font-mono text-[10px] text-muted-foreground", className)}>no slot</span>;
  return <span data-testid="slot-chip" className={cn("inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-secondary border border-border", className)}><Clock3 size={10} /> S{slot}{time ? ` · ${time}` : ""}</span>;
}

function RequestRows({ rows, unit }) {
  if (rows.length === 0) return <EmptyState title="No requests yet" hint="File one and it lands in your approver's inbox." />;
  return rows.map((r) => (
    <div key={r.id} className="ledger-row" style={{ gridTemplateColumns: "minmax(0,2fr) minmax(0,1.3fr) minmax(0,1fr) auto" }} data-testid={`request-row-${r.id}`}>
      <div><div className="font-medium text-sm">{r.title || r.category || titleCase(r.type)}</div><div className="text-xs text-muted-foreground truncate">{r.duration_type ? titleCase(r.duration_type) : ""}{r.duration_type && r.reason ? " · " : ""}{r.reason}</div></div>
      <div className="text-xs text-muted-foreground font-mono">{fmtDate(r.start_date)}{r.end_date && r.end_date !== r.start_date ? ` → ${fmtDate(r.end_date)}` : ""}</div>
      <div className="text-sm font-mono">{r.days ? `${r.days}d` : ""}{r.hours ? <span className="text-muted-foreground"> · {r.hours}h</span> : ""}</div>
      <div className="flex flex-col items-end gap-1"><Stamp status={r.status} />{r.decision_comment && <span className="text-[10px] text-muted-foreground italic max-w-[160px] truncate">“{r.decision_comment}”</span>}</div>
    </div>
  ));
}

/* ---------------- Leave ---------------- */
export function LeaveTab() {
  const [rows, setRows] = useState([]);
  const [ent, setEnt] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => { api.get("/requests/me", { params: { type: "leave" } }).then((r) => setRows(r.data)); api.get("/leave/entitlements").then((r) => setEnt(r.data)); };
  useEffect(load, []);
  return (
    <div className="space-y-5">
      {ent && (
        <div className="paper-card p-4 flex flex-wrap items-center gap-4 rise" data-testid="leave-slot-banner">
          <SlotChip slot={ent.slot} time={ent.slot ? `${["07:30", "07:45", "08:00", "08:15", "08:30", "08:45", "09:00", "09:15", "09:30"][ent.slot - 1]}` : null} />
          <span className="text-sm text-muted-foreground">Your working day is <b className="text-foreground">{ent.hours_per_day}h</b> based on your slot. Half-day = {ent.hours_per_day / 2}h · hourly leave is deducted as hours ÷ {ent.hours_per_day}.</span>
          <span className="ml-auto text-xs font-mono text-muted-foreground">service {ent.service.label}</span>
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4 stagger" data-testid="leave-entitlements">
        {(ent?.entitlements || []).map((e) => (
          <div key={e.leave_type_id} className="paper-card p-4" data-testid={`entitlement-${e.name.toLowerCase()}`}>
            <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ background: e.color || "#C85A32" }} /><span className="text-sm font-medium">{e.name}</span></div>
            <div className="mt-2 font-serif text-3xl">{e.remaining}<span className="text-sm text-muted-foreground">/{e.days}d</span></div>
            <div className="mt-2 h-1.5 rounded-full bg-secondary overflow-hidden"><div className="h-full rounded-full" style={{ width: `${e.days ? Math.min(100, (e.used / e.days) * 100) : 0}%`, background: e.color || "#C85A32" }} /></div>
            <div className="mt-1.5 text-[10px] font-mono text-muted-foreground truncate" title={e.rule}>{e.used}d used · {e.rule}</div>
          </div>
        ))}
      </div>
      <div className="paper-card overflow-hidden rise" data-testid="my-requests-leave">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-secondary/40">
          <span className="font-serif font-medium">Leave requests</span>
          <Button size="sm" className="rounded-full" data-testid="new-request-button" onClick={() => setOpen(true)}><Plus size={14} className="mr-1" /> New request</Button>
        </div>
        <RequestRows rows={rows} />
      </div>
      <LeaveDialog open={open} onOpenChange={setOpen} ent={ent} onDone={load} />
    </div>
  );
}

function LeaveDialog({ open, onOpenChange, ent, onDone }) {
  const [f, setF] = useState({ duration_type: "full_day", leave_type_id: "", start_date: "", end_date: "", hours: 2, title: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const hpd = ent?.hours_per_day || 8.5;
  const days = f.duration_type === "half_day" ? 0.5 : f.duration_type === "hourly" ? +(Number(f.hours || 0) / hpd).toFixed(3) : f.start_date && f.end_date ? Math.max(1, Math.round((new Date(f.end_date) - new Date(f.start_date)) / 86400000) + 1) : 1;
  const chosen = ent?.entitlements?.find((e) => e.leave_type_id === f.leave_type_id);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/requests", { type: "leave", ...f, hours: f.duration_type === "hourly" ? Number(f.hours) : undefined, end_date: f.duration_type === "full_day" ? f.end_date : f.start_date, title: f.title || `${chosen?.name || "Leave"} · ${titleCase(f.duration_type)}` });
      toast.success("Leave requested"); onOpenChange(false); onDone();
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="leave-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl font-medium">New leave request</DialogTitle><DialogDescription>Deducted according to your {hpd}h slot-based working day.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-3 gap-2" data-testid="duration-type-group">
            {[["full_day", "Full day", "1 day"], ["half_day", "Half day", `${hpd / 2}h`], ["hourly", "Hourly", "by hours"]].map(([k, l, h]) => (
              <button type="button" key={k} data-testid={`duration-${k}`} onClick={() => set("duration_type", k)} className={cn("rounded-xl border p-3 text-left transition-colors", f.duration_type === k ? "border-primary bg-primary/5" : "border-border hover:border-primary/40")}>
                <div className="text-sm font-medium">{l}</div><div className="text-[11px] text-muted-foreground font-mono">{h}</div>
              </button>
            ))}
          </div>
          <Field label="Leave type">
            <Select value={f.leave_type_id} onValueChange={(v) => set("leave_type_id", v)}>
              <SelectTrigger data-testid="field-leave_type_id"><SelectValue placeholder="Choose a type" /></SelectTrigger>
              <SelectContent>{(ent?.entitlements || []).map((e) => <SelectItem key={e.leave_type_id} value={e.leave_type_id}>{e.name} · {e.remaining}d left</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label={f.duration_type === "full_day" ? "From" : "Date"}><Input data-testid="field-start_date" type="date" required value={f.start_date} onChange={(e) => set("start_date", e.target.value)} /></Field>
            {f.duration_type === "full_day" ? <Field label="To"><Input data-testid="field-end_date" type="date" required value={f.end_date} onChange={(e) => set("end_date", e.target.value)} /></Field>
              : f.duration_type === "hourly" ? <Field label="Hours" hint={`max ${hpd}`}><Input data-testid="field-hours" type="number" min={0.5} max={hpd} step={0.5} value={f.hours} onChange={(e) => set("hours", e.target.value)} /></Field>
              : <Field label="Half"><div className="h-10 flex items-center text-sm text-muted-foreground font-mono">{hpd / 2} hours</div></Field>}
          </div>
          <Field label="Reason"><Textarea data-testid="field-reason" rows={2} value={f.reason} onChange={(e) => set("reason", e.target.value)} /></Field>
          <div className="flex items-center justify-between pt-1">
            <span className="text-sm" data-testid="leave-deduction-preview">Deducts <b className="font-serif text-lg">{days}</b> day{days === 1 ? "" : "s"}{chosen ? <span className="text-muted-foreground"> · {chosen.remaining - days >= 0 ? `${+(chosen.remaining - days).toFixed(2)} ${chosen.name} left after` : "exceeds balance"}</span> : ""}</span>
            <Button type="submit" data-testid="form-submit-button" disabled={busy || !f.leave_type_id} className="rounded-full px-5">{busy ? "Sending…" : "Submit request"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- WFH ---------------- */
export function WfhTab() {
  const [rows, setRows] = useState([]);
  const [w, setW] = useState(null);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ start_date: "", end_date: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const load = () => { api.get("/requests/me", { params: { type: "wfh" } }).then((r) => setRows(r.data)); api.get("/wfh/entitlement").then((r) => setW(r.data)); };
  useEffect(load, []);
  const submit = async (e) => { e.preventDefault(); setBusy(true); try { await api.post("/requests", { type: "wfh", ...f, title: "Work from home" }); toast.success("WFH requested"); setOpen(false); load(); } catch (err) { toast.error(errMsg(err)); } setBusy(false); };
  return (
    <div className="space-y-5">
      {w && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 stagger" data-testid="wfh-entitlement">
          <div className="paper-card p-5 md:col-span-1">
            <div className="flex items-center gap-2 text-muted-foreground"><Home size={14} /><span className="eyebrow !text-muted-foreground">WFH entitlement</span></div>
            {w.eligible ? <><div className="mt-2 font-serif text-3xl" data-testid="wfh-summary">{w.mode === "daily" ? "Daily" : `${w.remaining}/${w.days}d`}</div><div className="text-xs text-muted-foreground">{w.mode === "daily" ? "eligible every working day" : `remaining this ${w.period}`}</div></>
              : <><div className="mt-2 font-serif text-2xl text-muted-foreground" data-testid="wfh-summary">Not eligible</div><div className="text-xs text-muted-foreground">no WFH rule matches your profile yet</div></>}
          </div>
          <StatCard label="Service in current role" value={w.service.label} hint={`since ${fmtDate(w.service.since)}`} testId="wfh-service" />
          <StatCard label="Applied rule" value={w.rule || "—"} hint={w.note || (w.mode === "daily" ? "subject to policy" : w.eligible ? `${w.used}d used` : "")} accent="text-sage" testId="wfh-rule" />
        </div>
      )}
      <div className="paper-card overflow-hidden rise" data-testid="my-requests-wfh">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-secondary/40">
          <span className="font-serif font-medium">Work from home requests</span>
          <Button size="sm" className="rounded-full" data-testid="new-request-button" disabled={w && !w.eligible} onClick={() => setOpen(true)}><Plus size={14} className="mr-1" /> New request</Button>
        </div>
        <RequestRows rows={rows} />
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="wfh-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl font-medium">Request work from home</DialogTitle><DialogDescription>Counted against your {w?.period}ly WFH allowance.</DialogDescription></DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Field label="From"><Input data-testid="field-start_date" type="date" required value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} /></Field>
              <Field label="To"><Input data-testid="field-end_date" type="date" required value={f.end_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} /></Field>
            </div>
            <Field label="Reason"><Textarea data-testid="field-reason" rows={2} value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} /></Field>
            <div className="flex justify-end"><Button type="submit" data-testid="form-submit-button" disabled={busy} className="rounded-full px-5">{busy ? "Sending…" : "Submit request"}</Button></div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Bonus ---------------- */
export function BonusTab() {
  const [b, setB] = useState(null);
  useEffect(() => { api.get("/bonus/mine").then((r) => setB(r.data)); }, []);
  if (!b) return null;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 stagger">
        <div className="paper-card p-5 relative overflow-hidden">
          <Gift className="absolute -right-3 -bottom-3 text-primary/10" size={96} />
          <div className="eyebrow !text-muted-foreground">Tenure bonus</div>
          <div className="mt-2 font-serif text-4xl" data-testid="bonus-amount">{b.eligible ? fmtMoney(b.amount) : "—"}</div>
          <div className="text-xs text-muted-foreground">{b.eligible ? `${b.basis} · ${b.rule}` : "no bonus rule matches your profile"}</div>
        </div>
        <StatCard label="Service in current capacity" value={b.service.label} hint={`since ${fmtDate(b.service.since)}`} testId="bonus-service" />
        <StatCard label="Level · Position" value={`${b.level || "—"} · ${b.position || "—"}`} hint={b.designation} accent="text-sage" testId="bonus-level" />
      </div>
      {b.slab && (
        <div className="paper-card p-4 text-sm flex flex-wrap gap-4 items-center" data-testid="bonus-slab">
          <span className="eyebrow !text-muted-foreground">Your slab</span>
          <span className="font-mono">{b.slab.min_years}y – {b.slab.max_years ?? "∞"}y</span>
          <span className="font-medium">{b.slab.mode === "percent" ? `${b.slab.value}% of monthly salary` : fmtMoney(b.slab.value)}</span>
        </div>
      )}
      <div className="paper-card overflow-hidden rise" data-testid="bonus-assignments">
        <div className="px-4 py-3 border-b border-border bg-secondary/40 font-serif font-medium">Allocated bonuses & incentives</div>
        {b.assignments.length === 0 ? <EmptyState title="Nothing allocated yet" hint="HR / Finance allocations will appear here." /> : b.assignments.map((a) => (
          <div key={a.id} className="ledger-row" style={{ gridTemplateColumns: "minmax(0,2fr) 1fr 1fr auto" }}>
            <div><div className="font-medium text-sm">{titleCase(a.kind)} · {a.period}</div><div className="text-xs text-muted-foreground">{a.note}</div></div>
            <div className="text-xs text-muted-foreground">by {a.assigned_by}</div>
            <div className="text-xs font-mono text-muted-foreground">{fmtDate(a.created_at)}</div>
            <div className="font-serif text-lg">{fmtMoney(a.amount)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export { PageHeader };
